/**
 * The state behind a paged grid — which page is showing, and whether it may
 * turn itself. Split from `PagedGrid.tsx` because the page has **two** places
 * that read it: the grid itself, and the quiet pager that sits elsewhere on the
 * page (the homepage puts it in the filter row, by the count — owner ruling,
 * 1 October 2026: *"no side arrows over the grid"*). One hook, handed to both,
 * is what keeps them showing the same page. (It also keeps
 * `react-refresh/only-export-components` quiet: a hook beside a component in
 * one file breaks fast refresh.)
 *
 * **The pause rules are `Carousel`'s, unchanged** — the product owner approved
 * them on 15 September 2026 and the grid ruling kept them:
 *
 * - advances every `intervalMs`, wrapping from the last page to the first;
 * - pauses while the pointer is over the grid **or the pager**;
 * - pauses while keyboard focus is inside either;
 * - pauses while the browser tab is hidden;
 * - **never starts under `prefers-reduced-motion: reduce`** (the controls still
 *   work — this is not "slower", it is "not on its own");
 * - **stops for good the moment the reader presses previous or next** (*"unless
 *   the user interfered"*) — it does not resume when the pointer leaves.
 *
 * ⚠️ **`resetKey` re-arms everything.** The homepage passes the active tab:
 * switching from Events to Spaces is choosing new content, not steering the
 * old, so the grid starts again at page one with auto-play armed — exactly what
 * the carousel did when it was remounted per tab (`key={kind}`). It is a reset
 * during render (React's "storing information from previous renders" pattern),
 * not an effect, so there is no frame showing page three of the wrong kind.
 *
 * ⚠️ **"Focus is inside" must follow where focus really is, not the last event
 * seen** (Task 4 review I-1, reproduced live). The browser fires no `blur` when
 * the focused element is removed or natively `disabled`: the pager's own
 * "Next" did exactly that on the last page, focus fell to `<body>`, the flag
 * stayed true, and after a tab switch re-armed everything else the grid never
 * turned again. Three things keep it honest now — the pager buttons are never
 * natively disabled (`Pager`), a region that unmounts while holding focus or
 * the pointer lets go of it (the `ref` below), and while the flag is up any
 * focus landing outside every region clears it (`focusin` on the document).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { usePrefersReducedMotion } from './useMediaQuery'

export type PagedGridPauseHandlers = {
  /** Registers the region, so "focus is inside" can be checked against every
   *  region at once and released if the region unmounts. */
  ref: (element: HTMLElement | null) => (() => void) | undefined
  onPointerEnter: () => void
  onPointerLeave: () => void
  onFocus: () => void
  onBlur: (event: React.FocusEvent<HTMLElement>) => void
}

export type PagedGridController = {
  /** Zero-based. Always within `0 … pageCount - 1`. */
  page: number
  pageCount: number
  perPage: number
  atStart: boolean
  atEnd: boolean
  /** True once the reader has pressed previous or next since the last reset —
   *  auto-advance is then off for good, so every page change from here on is
   *  the reader's own and worth announcing (`Pager` does; it never announces
   *  an automatic turn, which would chatter every five seconds). */
  steered: boolean
  /** A reader's press: moves one page **and stops auto-advance for good**. */
  previous: () => void
  next: () => void
  /** Spread on every region whose hover or focus should hold the page still —
   *  the grid and the pager. */
  pauseHandlers: PagedGridPauseHandlers
}

export type UsePagedGridOptions = {
  itemCount: number
  perPage: number
  /** Milliseconds between automatic page turns. Omit to never turn alone. */
  intervalMs?: number | undefined
  /** Changing it returns to page one with auto-advance armed again. */
  resetKey?: string | undefined
}

function insideAny(regions: ReadonlySet<HTMLElement>, node: EventTarget | null): boolean {
  return node instanceof Node && [...regions].some((region) => region.contains(node))
}

export function usePagedGrid({
  itemCount,
  perPage,
  intervalMs,
  resetKey,
}: UsePagedGridOptions): PagedGridController {
  const safePerPage = Math.max(1, perPage)
  const pageCount = Math.max(1, Math.ceil(itemCount / safePerPage))

  const [page, setPage] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [tabHidden, setTabHidden] = useState(
    typeof document === 'undefined' ? false : document.visibilityState === 'hidden',
  )
  // Once true, stays true until `resetKey` changes — see the header.
  const [userTookControl, setUserTookControl] = useState(false)
  const reducedMotion = usePrefersReducedMotion()

  const [seenKey, setSeenKey] = useState(resetKey)
  if (seenKey !== resetKey) {
    setSeenKey(resetKey)
    setPage(0)
    setUserTookControl(false)
  }

  // The list can shrink (a retry, a narrower window giving more per page) —
  // never strand the reader on a page past the new end.
  const clampedPage = Math.min(page, pageCount - 1)

  // Every element spreading `pauseHandlers` — the grid and the pager.
  const regions = useRef(new Set<HTMLElement>())

  const register = useCallback((element: HTMLElement | null) => {
    if (!element) return undefined
    regions.current.add(element)
    return () => {
      regions.current.delete(element)
      // Removing the element under focus or the pointer fires neither `blur`
      // nor `pointerleave`. A pager with one page left renders nothing, so
      // this is a tab switch away, not an edge case.
      if (element.contains(document.activeElement)) setFocused(false)
      if (element.matches(':hover')) setHovered(false)
    }
  }, [])

  // While "focus is inside" is up, any focus that lands outside every region
  // proves it wrong — the recovery for focus that vanished without a `blur`
  // (a card removed under it, say) the moment the reader focuses anything.
  useEffect(() => {
    if (!focused) return
    const onFocusIn = (event: FocusEvent) => {
      if (!insideAny(regions.current, event.target)) setFocused(false)
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [focused])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = () => setTabHidden(document.visibilityState === 'hidden')
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  const canAutoPlay =
    Boolean(intervalMs) &&
    !reducedMotion &&
    !userTookControl &&
    !hovered &&
    !focused &&
    !tabHidden &&
    pageCount > 1

  useEffect(() => {
    if (!canAutoPlay || !intervalMs) return
    const id = window.setInterval(() => {
      setPage((current) => (Math.min(current, pageCount - 1) + 1) % pageCount)
    }, intervalMs)
    return () => window.clearInterval(id)
    // `page` is deliberately not a dependency: the updater reads it, and
    // depending on it would rebuild the interval on every tick.
  }, [canAutoPlay, intervalMs, pageCount])

  const atStart = clampedPage === 0
  const atEnd = clampedPage >= pageCount - 1

  return {
    page: clampedPage,
    pageCount,
    perPage: safePerPage,
    atStart,
    atEnd,
    steered: userTookControl,
    // A press at either end is a no-op, not a stop: the button stays focusable
    // there (see `Pager`), and pressing an unavailable control must change
    // nothing — including whether the grid may still turn on its own.
    previous: () => {
      if (atStart) return
      setUserTookControl(true)
      setPage(clampedPage - 1)
    },
    next: () => {
      if (atEnd) return
      setUserTookControl(true)
      setPage(clampedPage + 1)
    },
    pauseHandlers: {
      ref: register,
      onPointerEnter: () => setHovered(true),
      onPointerLeave: () => setHovered(false),
      onFocus: () => setFocused(true),
      onBlur: (event) => {
        // Only once focus has left every region — moving between two cards,
        // or straight from the pager into the grid, is still "focus within".
        if (!insideAny(regions.current, event.relatedTarget)) setFocused(false)
      },
    },
  }
}
